import type { DayHours } from "../api/types";

/**
 * When a takeaway or delivery is ready: placed + the preparation time of Algemeen (prototype SHOP.prepMin),
 * at the latest the closing of that day's opening hours, also for an order added after the closing
 * (Furkan, 11/10/2026: "Gewoon altijd laatste minuut van de sluiting pakken dan"). The same rule as the
 * server (server/src/modules/general/ready-time.ts), which prints the kitchen ticket and mails the
 * customer: the day starts at 06:00, a closing at or before the opening is after midnight, a closed day
 * has no cap.
 */

const DAY = 24 * 60;
const DAY_START_MINUTES = 6 * 60;
const toMinutes = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const clock = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Brussels",
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** Monday 0 … Sunday 6 and the minutes since midnight, in Belgium. */
function localMoment(at: Date): { weekday: number; minutes: number } {
  const parts = clock.formatToParts(at);
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "";
  return { weekday: WEEKDAYS.indexOf(part("weekday")), minutes: Number(part("hour")) * 60 + Number(part("minute")) };
}

/** The ready time of an order placed at `createdAt` that takes `prepMinutes`. */
export function readyTime(createdAt: Date, prepMinutes: number, hours: readonly DayHours[]): Date {
  const ready = new Date(createdAt.getTime() + prepMinutes * 60_000);
  let { weekday, minutes } = localMoment(createdAt);
  // before 06:00 is still the evening before
  if (minutes < DAY_START_MINUTES) {
    weekday = (weekday + 6) % 7;
    minutes += DAY;
  }
  const slot = hours[weekday];
  if (!slot) return ready;
  let closing = toMinutes(slot[1]);
  if (closing <= toMinutes(slot[0])) closing += DAY;
  if (minutes + prepMinutes <= closing) return ready;
  // the closing minute itself, counted from the start of the minute the order was placed in
  const minuteStart = createdAt.getTime() - (createdAt.getTime() % 60_000);
  return new Date(minuteStart + (closing - minutes) * 60_000);
}
